import React, { useState, useEffect, useRef } from 'react';
import {
  IoCloudDownloadOutline,
  IoChevronDown
} from 'react-icons/io5';
import { MdBookmark, MdBookmarkBorder } from 'react-icons/md';
import { BsEmojiSmile } from 'react-icons/bs';
import { FaUser, FaCheck } from 'react-icons/fa';
import { IniciativaFormatada } from 'types/iniciativa';
import iniciativaVideoImg from 'assets/img/2026/ELBB/iniciativa_video.png';
import { useAuth } from 'context/AuthContext';
import {
  registrarElogio,
  registrarFavorito
} from 'services/iniciativa-action.service';
import './IniciativaDetalhadaElbb.scss';

interface IniciativaDetalhadaElbbProps {
  iniciativa: IniciativaFormatada;
}

export const IniciativaDetalhadaElbb: React.FC<IniciativaDetalhadaElbbProps> = ({
  iniciativa
}) => {
  const { user } = useAuth();
  const [isComplimentOpen, setIsComplimentOpen] = useState(false);
  const [selectedCompliment, setSelectedCompliment] = useState<string>(iniciativa.usuarioElogio || '');
  const [isFavorited, setIsFavorited] = useState(iniciativa.usuarioFavoritou);
  const [loadingAction, setLoadingAction] = useState(false);
  const complimentRef = useRef<HTMLDivElement>(null);

  const complimentOptions = ['Arrasou!', 'Iniciativa inspiradora!', 'Superou expectativas!'];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (complimentRef.current && !complimentRef.current.contains(event.target as Node)) {
        setIsComplimentOpen(false);
      }
    };
    if (isComplimentOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isComplimentOpen]);

  const handleComplimentSelect = async (compliment: string) => {
    if (!user?._id || loadingAction) return;
    try {
      setLoadingAction(true);
      setSelectedCompliment(compliment);
      setIsComplimentOpen(false);
      await registrarElogio(user._id, {
        id_iniciativa: iniciativa.id,
        elogio: compliment
      });
    } catch (error) {
      setSelectedCompliment('');
    } finally {
      setLoadingAction(false);
    }
  };

  const handleToggleFavorite = async () => {
    if (!user?._id || loadingAction) return;
    try {
      setLoadingAction(true);
      const novoStatus = !isFavorited;
      await registrarFavorito(user._id, {
        id_iniciativa: iniciativa.id,
        ativo: novoStatus
      });
      setIsFavorited(novoStatus);
    } catch (error) {
      // Erro silencioso
    } finally {
      setLoadingAction(false);
    }
  };

  const handleDownload = () => {
    // TODO: Implementar lógica de download
  };

  return (
    <div className="iniciativa-detalhada-elbb" role="article" aria-label={`Iniciativa: ${iniciativa.titulo}`}>
      <div
        className={`iniciativa-detalhada-elbb__badge tag-${iniciativa.corCategoria}`}
        tabIndex={0}
        aria-label={`Categoria: ${iniciativa.categoria || iniciativa.tema || iniciativa.categoryPremio}`}
      >
        {iniciativa.categoria || iniciativa.tema || iniciativa.categoryPremio}
      </div>

      <div className="iniciativa-detalhada-elbb__body">
        <div className="iniciativa-detalhada-elbb__content-wrapper">
          {/* Coluna esquerda - Mídia e Feedback */}
          <div className="iniciativa-detalhada-elbb__left">
            {iniciativa.videoUrl && (
              <div className="iniciativa-detalhada-elbb__video-container">
                <a
                  href={iniciativa.originalVideoUrl || iniciativa.videoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block"
                  aria-label={`Assistir ao vídeo da iniciativa ${iniciativa.titulo} (abre em nova aba)`}
                >
                  <img
                    src={iniciativaVideoImg}
                    alt=""
                    aria-hidden="true"
                    className="iniciativa-detalhada-elbb__video-thumbnail"
                  />
                </a>
              </div>
            )}

            {iniciativa.documentacao && (
              <div className="iniciativa-detalhada-elbb__documentacao">
                <p className="iniciativa-detalhada-elbb__documentacao-label" tabIndex={0}>
                  Documentação complementar da realização
                </p>
                <button
                  className="iniciativa-detalhada-elbb__download-btn"
                  onClick={handleDownload}
                  aria-label="Baixar documentação complementar"
                >
                  <IoCloudDownloadOutline size={20} aria-hidden="true" />
                  <span>DOWNLOAD</span>
                </button>
              </div>
            )}

              {(iniciativa.elogiosPorTipo.length > 0 || iniciativa.totalFavoritos > 0) && (
              <div className="iniciativa-detalhada-elbb__feedback-box" role="region" aria-label="Feedback na iniciativa">
                <h3 className="iniciativa-detalhada-elbb__feedback-title" tabIndex={0}>Feedback na iniciativa:</h3>
                <div className="iniciativa-detalhada-elbb__feedback-list" role="list" aria-label="Lista de feedbacks">
                  {iniciativa.elogiosPorTipo.map((item, index) => (
                    <div key={index} className="iniciativa-detalhada-elbb__feedback-item" role="listitem" tabIndex={0} aria-label={`${item.quantidade} ${item.elogio}`}>
                      <span className="iniciativa-detalhada-elbb__feedback-count" aria-hidden="true">
                        {String(item.quantidade).padStart(2, '0')}
                      </span>
                      <span className="iniciativa-detalhada-elbb__feedback-label" aria-hidden="true">
                        {item.elogio}
                      </span>
                    </div>
                  ))}
                  {iniciativa.totalFavoritos > 0 && (
                    <div className="iniciativa-detalhada-elbb__feedback-item" role="listitem" tabIndex={0} aria-label={`${iniciativa.totalFavoritos} salvos nas favoritas`}>
                      <span className="iniciativa-detalhada-elbb__feedback-count" aria-hidden="true">
                        {String(iniciativa.totalFavoritos).padStart(2, '0')}
                      </span>
                      <span className="iniciativa-detalhada-elbb__feedback-label" aria-hidden="true">
                        Salvos nas favoritas
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Coluna direita - Informações */}
          <div className="iniciativa-detalhada-elbb__right">
            <h2 className="iniciativa-detalhada-elbb__category" tabIndex={0}>{iniciativa.titulo}</h2>
            <div className="iniciativa-detalhada-elbb__description">
              <p className="iniciativa-detalhada-elbb__subtitle" tabIndex={0}>
                {iniciativa.descricao}
              </p>
              <ol className="iniciativa-detalhada-elbb__strategic-list">
                <li>
                  <span className="iniciativa-detalhada-elbb__strategic-link" tabIndex={0}>
                    {iniciativa.direcionadorEstrategico || 'Diversificar e elevar o resultado considerando as necessidades e potencialidades dos clientes'}
                  </span>
                </li>
              </ol>

              <div className="iniciativa-detalhada-elbb__info-box" role="region" aria-label="Informações de cadastro">
                <div className="iniciativa-detalhada-elbb__info-item" tabIndex={0} aria-label={`Cadastrado por: ${iniciativa.cadastradoPor}, matrícula ${iniciativa.cadastradoMatricula}, em ${iniciativa.cadastradoData}`}>
                  <span className="iniciativa-detalhada-elbb__info-label">Cadastrado por:</span>
                  <span className="iniciativa-detalhada-elbb__info-value">
                    {iniciativa.cadastradoPor} ({iniciativa.cadastradoMatricula}), em {iniciativa.cadastradoData}
                  </span>
                </div>
                <div className="iniciativa-detalhada-elbb__info-item" tabIndex={0} aria-label={`Inscrito por: ${iniciativa.inscritoPor}, matrícula ${iniciativa.inscritoMatricula}, em ${iniciativa.inscritoData}`}>
                  <span className="iniciativa-detalhada-elbb__info-label">Inscrito por:</span>
                  <span className="iniciativa-detalhada-elbb__info-value">
                    {iniciativa.inscritoPor} ({iniciativa.inscritoMatricula}), em {iniciativa.inscritoData}
                  </span>
                </div>
              </div>

              <div className="iniciativa-detalhada-elbb__question">
                <h3 className="iniciativa-detalhada-elbb__question-title" tabIndex={0}>
                  Por que essa iniciativa foi fundamental para o atingimento do direcionador estratégico a ela vinculado
                  e que resultado ela gerou?
                </h3>
                <p className="iniciativa-detalhada-elbb__question-text" tabIndex={0}>
                  {iniciativa.texto}
                </p>
              </div>

              <div className="iniciativa-detalhada-elbb__participantes" role="region" aria-label="Participantes da realização">
                <h3 className="iniciativa-detalhada-elbb__participantes-title" tabIndex={0}>
                  Participantes da realização ({iniciativa.participantes.filter(p => p.confirmado).length} de{' '}
                  {iniciativa.participantes.length} confirmados)
                </h3>
                <div className="iniciativa-detalhada-elbb__participantes-grid" role="list" aria-label="Lista de participantes">
                  {iniciativa.participantes.map((participante, index) => (
                    <div
                      key={index}
                      className={`iniciativa-detalhada-elbb__participante ${
                        participante.confirmado ? 'confirmado' : 'pendente'
                      }`}
                      role="listitem"
                      tabIndex={0}
                      aria-label={`${participante.nome}, matrícula ${participante.matricula}. ${participante.confirmado ? 'Confirmado' : 'Pendente'}`}
                    >
                      <div className="iniciativa-detalhada-elbb__participante-content">
                        <FaUser className="iniciativa-detalhada-elbb__participante-avatar" size={18} aria-hidden="true" />
                        {participante.confirmado && (
                          <FaCheck className="iniciativa-detalhada-elbb__participante-check" size={18} aria-hidden="true" />
                        )}
                        <span className="iniciativa-detalhada-elbb__participante-nome">
                          {participante.nome}{' '}
                          <span className="iniciativa-detalhada-elbb__participante-matricula">
                            ({participante.matricula})
                          </span>
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Barra de ações */}
              <div className="iniciativa-detalhada-elbb__action-bar">
                <div className="iniciativa-detalhada-elbb__action-left">
                  <div className="iniciativa-detalhada-elbb__icon-wrapper">
                    <BsEmojiSmile
                      size={20}
                      aria-hidden="true"
                      style={{ color: selectedCompliment ? '#5D5FEF' : '#666666' }}
                    />
                    {iniciativa.totalElogios > 0 && (
                      <span className="iniciativa-detalhada-elbb__badge-count" aria-label={`${iniciativa.totalElogios} elogios`}>
                        {iniciativa.totalElogios}
                      </span>
                    )}
                  </div>
                  <div className="iniciativa-detalhada-elbb__compliment-container" ref={complimentRef}>
                    <button
                      className="iniciativa-detalhada-elbb__compliment-dropdown"
                      onClick={() => !loadingAction && setIsComplimentOpen(!isComplimentOpen)}
                      aria-expanded={isComplimentOpen}
                      aria-haspopup="listbox"
                      aria-label={selectedCompliment ? `Elogio selecionado: ${selectedCompliment}. Clique para alterar` : 'Faça um elogio'}
                      disabled={loadingAction}
                      style={{
                        opacity: loadingAction ? 0.6 : 1,
                        borderColor: selectedCompliment ? '#5D5FEF' : '#E0E7FF',
                        color: selectedCompliment ? '#5D5FEF' : '#666666'
                      }}
                    >
                      <span>{selectedCompliment || 'Faça um elogio!'}</span>
                      <IoChevronDown size={16} aria-hidden="true" />
                    </button>
                    {isComplimentOpen && !loadingAction && (
                      <div className="iniciativa-detalhada-elbb__compliment-menu" role="listbox" aria-label="Opções de elogio">
                        {complimentOptions.map((compliment) => (
                          <button
                            key={compliment}
                            className="iniciativa-detalhada-elbb__compliment-option"
                            onClick={() => handleComplimentSelect(compliment)}
                            role="option"
                            aria-selected={selectedCompliment === compliment}
                          >
                            {compliment}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="iniciativa-detalhada-elbb__action-right">
                  <div className="iniciativa-detalhada-elbb__icon-wrapper">
                    <button
                      className="iniciativa-detalhada-elbb__action-icon-btn"
                      aria-label={isFavorited ? 'Remover dos favoritos' : 'Salvar nos favoritos'}
                      aria-pressed={isFavorited}
                      onClick={handleToggleFavorite}
                      disabled={loadingAction}
                      style={{
                        opacity: loadingAction ? 0.6 : 1,
                        color: isFavorited ? '#5D5FEF' : '#666666'
                      }}
                    >
                      {isFavorited ? <MdBookmark size={20} aria-hidden="true" /> : <MdBookmarkBorder size={20} aria-hidden="true" />}
                    </button>
                    {iniciativa.totalFavoritos > 0 && (
                      <span className="iniciativa-detalhada-elbb__badge-count" aria-label={`${iniciativa.totalFavoritos} salvos nos favoritos`}>
                        {iniciativa.totalFavoritos}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer - Iniciativa Inscrita */}
      <div className="iniciativa-detalhada-elbb__footer">
        <span className="iniciativa-detalhada-elbb__status-btn" tabIndex={0} role="status">
          Iniciativa Inscrita
        </span>
      </div>
    </div>
  );
};
